import React, { useState } from 'react';
import { CountryPicker } from 'Shared/CountryPicker';
import styled from 'styled-components';
import { rem } from 'polished';
import { Alert } from '@unmind/design-system';
import LoadingIndicator from 'Shared/LoadingIndicator';
import { ApolloQueryResult } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  useCountries,
  useSetUserSelectedLocation,
  mapAndFilterCountries,
  ALL_OTHER_COUNTRIES_CODE,
} from 'Shared/location';
import { getUserCurrentLocation } from 'Shared/location/__generated__/getUserCurrentLocation';
import { getCountryContact } from './hooks/__generated__/getCountryContact';
import { getEapServices } from './hooks/__generated__/getEapServices';

const CountryPickerContainer = styled.div<{ inModal?: boolean }>`
  display: flex;
  flex-direction: column;
  max-width: ${rem(428)};
  height: 100%;
  justify-content: center;
  align-items: center;
  padding: 0px ${rem(24)} ${rem(24)};
  gap: ${rem(16)};
  > div:first-child {
    padding: ${({ inModal }) => (inModal ? `${rem(24)} 0px 0px` : rem(24))};
    background: ${({ inModal, theme }) =>
      inModal ? theme.colors.background.primary : theme.colors.background.card};
  }
`;

const CountryPickerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-self: center;
  border-radius: ${rem(20)};
  width: fit-content;
  height: fit-content;
`;

const ErrorContainer = styled.div`
  display: flex;
`;

type HelpLocationSelectionProps = {
  refetchUserLocation(): Promise<ApolloQueryResult<getUserCurrentLocation>>;
  refetchCountryContact(): Promise<ApolloQueryResult<getCountryContact>>;
  refetchEapServices(): Promise<ApolloQueryResult<getEapServices>>;
  inModal?: boolean;
  onClose?(): void;
};

const HelpLocationSelect = ({
  refetchUserLocation,
  refetchCountryContact,
  refetchEapServices,
  inModal,
  onClose: handleClose,
}: HelpLocationSelectionProps) => {
  const [error, setError] = useState('');
  const [disableSubmit, setDisableSubmit] = useState(false);
  const { data, loading: isLoadingCountries } = useCountries();

  const { t: translate } = useTranslation('help', {
    keyPrefix: 'country_picker',
  });

  const { setLocation, loading: isLoadingSetUserSelectedLocation } =
    useSetUserSelectedLocation({
      source: 'help',
      onCompleted: async success => {
        if (!success) {
          setDisableSubmit(false);
          setError('There was an error updating your location.');

          return;
        }

        await Promise.all([
          refetchUserLocation(),
          refetchCountryContact(),
          refetchEapServices(),
        ]);
        if (handleClose) {
          handleClose();
        }
      },
      onError: updateError => {
        setDisableSubmit(false);
        setError(`There was an error updating your location: ${updateError}`);
      },
    });

  if (isLoadingCountries || isLoadingSetUserSelectedLocation) {
    return <LoadingIndicator />;
  }

  const countryOptions = mapAndFilterCountries(data?.countries || [], [
    ALL_OTHER_COUNTRIES_CODE,
  ]);

  const submitCountry = async (countryCode: string) => {
    setDisableSubmit(true);
    const country = countryOptions.find(option => option.value === countryCode);
    await setLocation(countryCode, country?.label);
  };

  return (
    <CountryPickerContainer inModal={inModal}>
      <CountryPickerWrapper>
        <CountryPicker
          countryOptions={countryOptions}
          disabled={disableSubmit || isLoadingSetUserSelectedLocation}
          onSubmit={submitCountry}
          inModal={inModal}
          displayCloseButton={false}
        />
      </CountryPickerWrapper>

      {error && (
        <ErrorContainer>
          <Alert
            title={translate('error.title')}
            body={translate('error.body')}
            variant="error"
            size="small"
          />
        </ErrorContainer>
      )}
    </CountryPickerContainer>
  );
};

export default HelpLocationSelect;
